// AdminDashboard — At-a-glance metrics, charts, alerts, queue summaries

export function AdminDashboard() {
  // Mock live metrics
  const metrics = {
    activeUsers: 12847,
    activeNow: 1243,
    todayPnl: 184320,
    todayTrades: 8420,
    pendingKyc: 23,
    pendingPayouts: 8,
    openChallenges: 412,
    fundedTraders: 1847,
    dailyRevenue: 26480,
    errorRate: 0.02,
  };

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-bold text-white">Dashboard</h1>
          <p className="text-[12px] text-white/50">Overview of platform health and activity</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="h-9 rounded-[8px] bg-white/5 border border-white/10 px-3 text-[12px] text-white focus:outline-none" data-testid="time-range">
            <option>Last 24h</option>
            <option>Last 7d</option>
            <option>Last 30d</option>
          </select>
        </div>
      </div>

      {/* Top metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid">
        <KpiCard label="Active users" value={metrics.activeUsers.toLocaleString()} sub={`${metrics.activeNow} online now`} icon="👥" color="#007AFF" trend="+12%" />
        <KpiCard label="Today P&L" value={`$${(metrics.todayPnl / 1000).toFixed(1)}K`} sub="All users" icon="📈" color="#34C759" trend="+8.4%" />
        <KpiCard label="Today trades" value={metrics.todayTrades.toLocaleString()} sub="Across 287 traders" icon="📊" color="#FF9500" trend="+24%" />
        <KpiCard label="Daily revenue" value={`$${(metrics.dailyRevenue / 1000).toFixed(1)}K`} sub="From fees" icon="💰" color="#FF3B30" trend="+5.2%" />
      </div>

      {/* Live activity chart */}
      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/40">Active traders · last 24h</div>
            <div className="mt-1 text-[24px] font-bold text-white tabular-nums">1,243 <span className="text-[12px] text-[#34C759] ml-1">+18%</span></div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#34C759] animate-pulse" />
            <span className="text-[11px] text-white/60">Live</span>
          </div>
        </div>
        <svg viewBox="0 0 600 100" preserveAspectRatio="none" className="h-24 w-full">
          {(() => {
            const points: number[] = [];
            for (let i = 0; i < 48; i++) {
              const peak = i > 12 && i < 36 ? 1.4 : 1;
              points.push(60 + Math.sin(i * 0.3) * 15 * peak + Math.random() * 12 * peak);
            }
            const min = Math.min(...points), max = Math.max(...points) - min || 1;
            const path = points.map((p, i) => { const x = (i / (points.length - 1)) * 600; const y = 100 - ((p - min) / max) * 80 - 10; return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`; }).join(' ');
            return (
              <>
                <defs><linearGradient id="admin-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF3B30" stopOpacity="0.3" /><stop offset="100%" stopColor="#FF3B30" stopOpacity="0" /></linearGradient></defs>
                <path d={path + ' L600 100 L0 100 Z'} fill="url(#admin-fill)" />
                <path d={path} fill="none" stroke="#FF3B30" strokeWidth="2" />
              </>
            );
          })()}
        </svg>
      </div>

      {/* Queue summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <QueueCard
          title="KYC queue" emoji="🛡️" count={metrics.pendingKyc} color="#FF9500"
          desc="23 users waiting for verification"
          actions={['Review queue', 'Bulk approve all']}
        />
        <QueueCard
          title="Payout requests" emoji="💰" count={metrics.pendingPayouts} color="#34C759"
          desc="8 withdrawals pending approval ($12,400 total)"
          actions={['Review payouts']}
        />
        <QueueCard
          title="Flagged content" emoji="🚩" count={4} color="#FF3B30"
          desc="4 posts/comments awaiting review"
          actions={['Review flagged']}
        />
        <QueueCard
          title="Open challenges" emoji="🎯" count={metrics.openChallenges} color="#007AFF"
          desc="412 active challenges in evaluation"
          actions={['View challenges', 'Live monitor']}
        />
      </div>

      {/* System health */}
      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-5">
        <h2 className="text-[14px] font-bold text-white mb-3">System health</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Health label="API latency" val="42ms" status="good" />
          <Health label="Error rate"  val={`${metrics.errorRate}%`} status="good" />
          <Health label="Uptime (30d)" val="99.97%" status="good" />
          <Health label="Active sessions" val="1,243" status="good" />
        </div>
      </div>

      {/* Recent activity */}
      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[14px] font-bold text-white">Recent activity</h2>
          <button className="text-[11px] text-[#FF3B30] font-semibold">View all →</button>
        </div>
        <div className="space-y-2">
          {[
            { t: '2m ago',  a: 'New KYC submission',  who: 'Aisha Khan',     color: '#FF9500' },
            { t: '4m ago',  a: 'Payout requested',    who: '$1,240 · Brandon Lee', color: '#34C759' },
            { t: '8m ago',  a: 'New user signup',     who: 'Sofia Petrov',   color: '#007AFF' },
            { t: '12m ago', a: 'Failed login',        who: 'demo@funderpro.live', color: '#FF3B30' },
            { t: '18m ago', a: 'Bot published',       who: 'Aiden Wright · NQ Sniper', color: '#AF52DE' },
            { t: '23m ago', a: 'Challenge passed',    who: 'Yuki Tanaka · $50K', color: '#34C759' },
          ].map((e, i) => (
            <div key={i} data-testid={`event-${i}`} className="flex items-center gap-3 border-l-2 pl-3 py-1.5" style={{ borderColor: e.color }}>
              <div className="text-[10px] text-white/40 tabular-nums w-14 shrink-0">{e.t}</div>
              <div className="flex-1">
                <div className="text-[12px] text-white font-medium">{e.a}</div>
                <div className="text-[10px] text-white/50">{e.who}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, icon, color, trend }: { label: string; value: string; sub: string; icon: string; color: string; trend: string }) {
  return (
    <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4">
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
        <div className="flex h-7 w-7 items-center justify-center rounded-[8px] text-[14px]" style={{ backgroundColor: color + '20' }}>{icon}</div>
      </div>
      <div className="mt-2 text-[24px] font-bold text-white tabular-nums">{value}</div>
      <div className="mt-0.5 flex items-center justify-between">
        <div className="text-[10px] text-white/50">{sub}</div>
        <div className="text-[10px] text-[#34C759] font-semibold">{trend}</div>
      </div>
    </div>
  );
}

function QueueCard({ title, emoji, count, color, desc, actions }: { title: string; emoji: string; count: number; color: string; desc: string; actions: string[] }) {
  return (
    <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-[12px] text-[20px]" style={{ backgroundColor: color + '20' }}>{emoji}</div>
        <div className="flex-1">
          <div className="text-[13px] font-semibold text-white">{title}</div>
          <div className="text-[10px] text-white/50">{desc}</div>
        </div>
        <div className="text-[24px] font-bold tabular-nums" style={{ color }}>{count}</div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {actions.map((a) => (
          <button key={a} className="rounded-full bg-white/5 px-3 py-1 text-[11px] font-semibold text-white/80 active:bg-white/10">{a}</button>
        ))}
      </div>
    </div>
  );
}

function Health({ label, val, status }: { label: string; val: string; status: 'good' | 'warn' | 'bad' }) {
  const c = status === 'good' ? '#34C759' : status === 'warn' ? '#FF9500' : '#FF3B30';
  return (
    <div className="rounded-[10px] bg-white/5 p-3 flex items-center gap-2">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c }} />
      <div>
        <div className="text-[10px] uppercase text-white/50">{label}</div>
        <div className="text-[14px] font-bold text-white tabular-nums">{val}</div>
      </div>
    </div>
  );
}
